import type { Operation, QueryParams, ResponseBody } from '@platform/openapi';
import { PRODUCT_STATUSES } from '../product.ts';
import type { OperationPeriodChoice, OperationWindow } from './operation-period.ts';

type ListQuery = NonNullable<QueryParams<Operation<'/admin/products', 'get'>>>;

export type ProductFilterStatus = NonNullable<ListQuery['status']>;
export type ProductStockFilter = NonNullable<ListQuery['stock']>;
export type ProductSort = NonNullable<ListQuery['sort']>;
export type ProductOperation = NonNullable<ListQuery['operation']>;
export type ProductCounts = ResponseBody<Operation<'/admin/products/counts', 'get'>>;

export const PRODUCT_FILTER_STATUSES = [
  'all',
  ...PRODUCT_STATUSES,
] as const satisfies readonly ProductFilterStatus[];

export const PRODUCT_STOCK_FILTERS = ['low', 'out'] as const satisfies readonly ProductStockFilter[];

export const PRODUCT_OPERATIONS = [
  'sale',
  'return',
  'defective',
  'loss',
] as const satisfies readonly ProductOperation[];

export const PRODUCT_SORTS = [
  'created:desc',
  'created:asc',
  'updated:desc',
  'name:asc',
] as const satisfies readonly ProductSort[];

export const PRODUCT_VIEWS = [
  'all',
  'active',
  'draft',
  'archived',
  'low',
  'out',
  ...PRODUCT_OPERATIONS,
] as const;

export type ProductView = (typeof PRODUCT_VIEWS)[number];

export type ProductFilters = {
  readonly status: ProductFilterStatus;
  readonly stock: ProductStockFilter | null;
  readonly categoryId: string | null;
  readonly optionValueId: string | null;
  readonly company: string | null;
  readonly search: string | null;
  readonly operation: ProductOperation | null;
  readonly sort: ProductSort;
} & OperationPeriodChoice;

export const PAGE_SIZE = 25;

const isStockView = (view: ProductView): view is ProductStockFilter =>
  PRODUCT_STOCK_FILTERS.some((stock) => stock === view);

const isOperationView = (view: ProductView): view is ProductOperation =>
  PRODUCT_OPERATIONS.some((operation) => operation === view);

export const viewOf = (filters: ProductFilters): ProductView =>
  filters.operation ?? filters.stock ?? filters.status;

export const withView = (filters: ProductFilters, view: ProductView): ProductFilters => {
  if (isOperationView(view)) return { ...filters, status: 'all', stock: null, operation: view };
  if (isStockView(view)) return { ...filters, status: 'all', stock: view, operation: null };
  return { ...filters, status: view, stock: null, operation: null };
};

type StatusView = Exclude<ProductView, ProductOperation>;

const COUNT_OF_VIEW: Readonly<Record<StatusView, Exclude<keyof ProductCounts, 'operations'>>> = {
  all: 'all',
  active: 'active',
  draft: 'draft',
  archived: 'archived',
  low: 'lowStock',
  out: 'outOfStock',
};

export const countOfView = (counts: ProductCounts | undefined, view: ProductView): number | undefined =>
  isOperationView(view) ? (counts?.operations?.[view] ?? undefined) : counts?.[COUNT_OF_VIEW[view]];

export const listQuery = (
  filters: ProductFilters,
  pageToken: string | null,
  operationWindow: OperationWindow | null,
): ListQuery => ({
  status: filters.status,
  sort: filters.sort,
  page_size: PAGE_SIZE,
  ...(filters.stock === null ? {} : { stock: filters.stock }),
  ...(filters.categoryId === null ? {} : { category_id: filters.categoryId }),
  ...(filters.optionValueId === null ? {} : { option_value_id: filters.optionValueId }),
  ...(filters.company === null ? {} : { company: filters.company }),
  ...(filters.search === null ? {} : { search: filters.search }),
  ...(filters.operation === null || operationWindow === null
    ? {}
    : {
        operation: filters.operation,
        operation_since: operationWindow.since,
        ...(operationWindow.until === null ? {} : { operation_until: operationWindow.until }),
      }),
  ...(pageToken === null ? {} : { page_token: pageToken }),
});
